import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Loader2, RefreshCw, Trash2, X, UserCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination, type PageMeta } from '@/components/Pagination';
import { apiCall, type CheckIn } from '@/lib/api';
import { wiqIdsFromNotes, wiqProfileUrl } from '@/lib/utils';

// Kiosk rows are ISO with a zone (…Z); dashboard/public rows are SQLite 'YYYY-MM-DD HH:MM:SS' UTC.
function parseCheckinTime(iso: string): Date {
  const s = iso.replace(' ', 'T');
  return new Date(/(Z|[+-]\d\d:?\d\d)$/.test(s) ? s : s + 'Z');
}

// The gym is in California: show exact times in Pacific (PDT/PST as applicable).
const pacificFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric',
  hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
});
const pacificTime = (iso: string) => pacificFormat.format(parseCheckinTime(iso));

function relativeTime(iso: string): string {
  const d = parseCheckinTime(iso);
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

// Set per check-in by apps/marketing/scripts/syncCheckinCoverage.ts as
// `coverage=<kind>[:<detail>]` in notes, using the daily-report classification.
const COVERAGE: Record<string, { label: string; className: string }> = {
  subscription: { label: 'Subscription', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  'class-pass': { label: 'Class pass', className: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  cash: { label: 'Cash', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  exception: { label: 'Exception', className: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300' },
  none: { label: 'Not covered', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
};

// Date (YYYY-MM-DD) the member's last class pass was used, when this check-in
// was uncovered because their passes ran out.
const passesUsedUpOn = (notes: string | null) => (notes ?? '').match(/coverage=none:passes-used-up@([\d-]{10}|\?)/)?.[1];

const isCashOverdue = (notes: string | null) => /coverage=none:cash overdue/.test(notes ?? '');

type UncoveredReason = 'canceled' | 'no-plan' | 'passes' | 'cash' | 'other';
const UNCOVERED_REASONS: { key: UncoveredReason; label: string }[] = [
  { key: 'canceled', label: 'Subscription Canceled' },
  { key: 'no-plan', label: 'No plan' },
  { key: 'passes', label: 'Out of passes' },
  { key: 'cash', label: 'Cash overdue' },
];
const uncoveredReason = (notes: string | null): UncoveredReason => {
  const n = notes ?? '';
  if (n.includes('coverage=none:canceled subscription')) return 'canceled';
  if (n.includes('coverage=none:no plan')) return 'no-plan';
  if (n.includes('coverage=none:passes-used-up')) return 'passes';
  if (n.includes('coverage=none:cash overdue')) return 'cash';
  return 'other';
};

const NONE_REASON: Record<string, string> = {
  'canceled subscription': 'canceled',
  'no plan': 'no plan',
  'cash overdue': 'cash overdue',
};

function CoverageBadge({ notes }: { notes: string | null }) {
  const m = (notes ?? '').match(/coverage=([a-z-]+)(?::([^;]+))?/);
  const c = m?.[1] ? COVERAGE[m[1]] : undefined;
  if (!c) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${c.className}`} title={m?.[2]?.trim() || c.label}>
      {passesUsedUpOn(notes)
        ? `Out of passes (last used ${passesUsedUpOn(notes)})`
        : m?.[1] === 'none' && m?.[2] ? `${c.label} · ${NONE_REASON[m[2].trim()] ?? m[2].trim()}` : c.label}
    </span>
  );
}

export default function CheckIns() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<CheckIn[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta | null>(null);
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<'48h' | 'all' | 'uncovered' | 'canceled' | 'passes'>('48h');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [hideCash, setHideCash] = useState<boolean>(() => {
    try { return localStorage.getItem('checkins.hideCash') === '1'; } catch { return false; }
  });
  const toggleHideCash = (v: boolean) => {
    setHideCash(v);
    try { localStorage.setItem('checkins.hideCash', v ? '1' : '0'); } catch { /* storage unavailable */ }
  };
  // Not covered tab: a reason pill narrows the list; "Hide cash" applies to All.
  const [reason, setReason] = useState<UncoveredReason | 'all'>('all');
  const reasonCounts = scope === 'uncovered'
    ? items.reduce<Record<string, number>>((acc, c) => { const r = uncoveredReason(c.notes); acc[r] = (acc[r] ?? 0) + 1; return acc; }, {})
    : {};
  const shown = scope !== 'uncovered' ? items
    : reason !== 'all' ? items.filter((c) => uncoveredReason(c.notes) === reason)
    : hideCash ? items.filter((c) => !isCashOverdue(c.notes)) : items;

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = await getToken();
      const query: Record<string, string | number> = { page, per_page: 50 };
      if (scope === '48h') {
        query.from = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
      }
      if (scope === 'passes' || scope === 'uncovered' || scope === 'canceled') {
        // Coverage lives in notes, so these tabs load the past 7 days and filter here.
        const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const all: CheckIn[] = [];
        for (let p = 1; p <= 10; p++) {
          const r = await apiCall<{ items: CheckIn[] } & PageMeta>('/v1/check_ins', {
            bearerToken: token, query: { page: p, per_page: 200, from: since.toISOString() },
          });
          all.push(...r.items);
          if (p >= r.total_pages) break;
        }
        if (scope === 'uncovered') {
          setItems(all.filter((c) => /coverage=none\b/.test(c.notes ?? '')));
        } else if (scope === 'canceled') {
          setItems(all.filter((c) => uncoveredReason(c.notes) === 'canceled'));
        } else {
          // Check-ins by members who had class passes but have none left now
          // (coverage=none:passes-used-up@<last pass used>), regardless of when
          // the last one was used. Newest check-in first.
          setItems(all.filter((c) => passesUsedUpOn(c.notes) !== undefined));
        }
        setPageMeta(null);
        return;
      }
      const r = await apiCall<{ items: CheckIn[] } & PageMeta>('/v1/check_ins', {
        bearerToken: token, query,
      });
      setItems(r.items);
      setPageMeta({ page: r.page, per_page: r.per_page, total: r.total, total_pages: r.total_pages });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }, [getToken, page, scope]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [scope]);

  async function doDelete(id: string) {
    const token = await getToken();
    await apiCall(`/v1/check_ins/${id}`, { method: 'DELETE', bearerToken: token });
    setItems((prev) => prev.filter((x) => x.id !== id));
    setPendingDelete(null);
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Check-ins</h1>
          <p className="text-sm text-muted-foreground">
            {scope === 'canceled' ? (loading ? 'Loading…' : `${items.length} check-ins in the last 7 days on a canceled subscription`) : scope === 'uncovered' ? (loading ? 'Loading…' : `${shown.length} uncovered check-ins in the last 7 days${reason === 'all' && hideCash ? ' (cash hidden)' : ''}`) : scope === 'passes' ? (loading ? 'Loading…' : `${items.length} check-ins in the last 7 days with no passes left`) : pageMeta ? `${pageMeta.total} total` : 'Loading…'} · public page at{' '}
            <a href="/checkin" target="_blank" rel="noreferrer" className="underline">/checkin</a>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="border-b flex items-center gap-1">
        {(['48h', 'all', 'uncovered', 'canceled', 'passes'] as const).map((s) => (
          <button
            key={s} onClick={() => setScope(s)}
            className={
              'px-3 py-2 text-sm border-b-2 -mb-px ' +
              (scope === s ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')
            }>{s === '48h' ? 'Last 48 hours' : s === 'all' ? 'All time' : s === 'uncovered' ? 'Not covered (7d)' : s === 'canceled' ? 'Subscription canceled (7d)' : 'Ran out of passes (7d)'}</button>
        ))}
      {scope === 'uncovered' && (
          <label className="ml-auto flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground cursor-pointer select-none">
            <input type="checkbox" checked={hideCash} onChange={(e) => toggleHideCash(e.target.checked)} className="h-4 w-4" />
            Hide cash wrestlers
          </label>
        )}
      </div>

      {scope === 'uncovered' && !loading && items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {[{ key: 'all' as const, label: 'All', n: hideCash ? items.length - (reasonCounts.cash ?? 0) : items.length },
            ...UNCOVERED_REASONS.map((r) => ({ ...r, n: reasonCounts[r.key] ?? 0 }))].map((p) => (
            <button key={p.key} onClick={() => setReason(p.key)}
              className={'rounded-full border px-3 py-1 text-xs transition-colors ' +
                (reason === p.key ? 'bg-foreground text-background border-foreground' : 'text-muted-foreground hover:text-foreground hover:border-foreground/40')}>
              {p.label} <span className="ml-1 opacity-70">{p.n}</span>
            </button>
          ))}
        </div>
      )}

      {err && <Card><CardContent className="py-4 text-sm text-destructive">{err}</CardContent></Card>}

      {!err && !loading && shown.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <UserCheck className="mx-auto mb-3 h-8 w-8 opacity-40" />
            {scope === '48h' ? 'No check-ins in the last 48 hours.' : scope === 'passes' ? 'No check-ins with zero passes left in the last 7 days.' : scope === 'uncovered' ? 'No uncovered check-ins in the last 7 days.' : scope === 'canceled' ? 'No check-ins on a canceled subscription in the last 7 days.' : 'No check-ins yet.'}
          </CardContent>
        </Card>
      )}

      {shown.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="px-3 py-2 font-medium">Visitor</th>
                  <th className="px-3 py-2 font-medium">Covered by</th>
                  <th className="px-3 py-2 font-medium">Class</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => {
                  const isDel = pendingDelete === c.id;
                  const name = c.member_name ?? c.day_pass_name ?? c.visitor_name ?? '(unknown)';
                  const kind = c.member_id ? 'member' : c.day_pass_id ? 'day pass' : 'guest';
                  return (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-3 py-2 font-medium">
                        {name}
                        {wiqIdsFromNotes(c.notes).map((id) => (
                          <a key={id} href={wiqProfileUrl(id)} target="_blank" rel="noreferrer"
                            className="ml-2 text-xs font-normal text-muted-foreground hover:text-foreground hover:underline"
                            title={`Open WrestlingIQ profile ${id}`}>WIQ ↗</a>
                        ))}
                      </td>
                      <td className="px-3 py-2"><CoverageBadge notes={c.notes} /></td>
                      <td className="px-3 py-2 text-xs">{c.class_name ?? <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2"><Badge variant={c.member_id ? 'default' : 'secondary'}>{kind}</Badge></td>
                      <td className="px-3 py-2"><Badge variant="outline">{c.source}</Badge></td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap" title={c.checked_in_at}>
                        <div>{pacificTime(c.checked_in_at)}</div>
                        <div className="opacity-70">{relativeTime(c.checked_in_at)}</div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isDel ? (
                          <div className="inline-flex items-center gap-1">
                            <Button variant="destructive" size="sm" className="h-7 px-2" onClick={() => void doDelete(c.id)}>Yes</Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setPendingDelete(null)}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => setPendingDelete(c.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Pagination meta={pageMeta} onPageChange={setPage} itemLabel="check-ins" />
    </div>
  );
}
